import { apiClient } from './client';
import type {
  CustomerResult,
  MissionSummary,
  MissionReconciliationData,
  ReconciliationRecord,
} from '@/types';

export async function fetchMissions(): Promise<MissionSummary[]> {
  return apiClient.get<MissionSummary[]>('/api/inventory/missions');
}

/**
 * Manager/admin override of a single location's customer-facing report fields.
 *
 * PATCH /api/inventory/records/{record_id}/result
 *   body: { result, comments, expected_count?, drone_count? }
 *
 * All fields are optional server-side; the caller sends whatever the editor
 * changed. expected_count → record.expected_boxes (SOH Count) and drone_count →
 * record.ai_counted_cartons (Drone Count). Returns the updated record (same
 * shape as a flat_records entry) so the caller can refresh the chip colour,
 * comment, counts, and header totals. Customers never call this — the UI hides
 * the controls for the customer role.
 */
export async function updateRecordResult(
  recordId: string,
  body: {
    result: CustomerResult;
    comments: string;
    expected_count?: number | null;
    drone_count?: number | null;
    damaged_racking?: boolean;
    damaged_racking_note?: string | null;
    detected_sku?: string | null;
    expected_sku?: string | null;
    item_code?: string | null;
    item_description?: string | null;
  },
): Promise<ReconciliationRecord> {
  return apiClient.patch<ReconciliationRecord>(
    `/api/inventory/records/${recordId}/result`,
    body,
  );
}

export async function fetchMissionRecords(
  missionId: string,
): Promise<MissionReconciliationData> {
  return apiClient.get<MissionReconciliationData>(
    `/api/inventory/missions/${missionId}/records`,
  );
}

/** Returns a blob URL for the drone photo.  Caller must call URL.revokeObjectURL() on unmount. */
export async function fetchPhotoUrl(photoId: string): Promise<string> {
  const response = await apiClient.getRaw(`/api/inventory/photos/${photoId}/image`);
  if (!response.ok) throw new Error('Photo not found');
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/** Build the image URL directly from the canonical filename served via the inventory router. */
export function buildPhotoUrl(photoId: string): string {
  return `/api/inventory/photos/${photoId}/image`;
}

/** Build the ~320px list-thumbnail URL. Use for card/row previews — at 2,000+
 *  locations the full-res /image per card exhausts browser blob memory. */
export function buildPhotoThumbUrl(photoId: string): string {
  return `/api/inventory/photos/${photoId}/thumb`;
}

/** Returns the URL for the Excel export download (used as an <a href> target). */
export function missionExportUrl(missionId: string): string {
  return `/api/inventory/missions/${missionId}/export.xlsx`;
}
